/**
 * /app/$tenantSlug/wfh/approvals — WFH approvals queue (migration 063).
 *
 * Manager / HR surface. The list query is role-scoped server-side
 * (manager = own + reports; hr_admin = tenant), so this page just renders
 * the pending queue and the approve/reject actions. Approving overlays
 * attendance for the covered days (server-side) — no balance moves.
 */

import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { LeaveFeatureGate } from "@/components/leave/leave-feature-gate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  useWFHRequests,
  useApproveWFHRequest,
  useRejectWFHRequest,
} from "@/hooks/use-wfh";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/wfh/approvals",
)({
  component: WFHApprovalsRoute,
});

function WFHApprovalsRoute() {
  const q = useWFHRequests("pending");
  const approve = useApproveWFHRequest();
  const reject = useRejectWFHRequest();

  return (
    <TimeAndLeaveShell
      title="WFH approvals"
      description="WFH requests awaiting your sign-off. Approving does not use any leave balance."
    >
      <LeaveFeatureGate>
        <Card>
          <CardHeader>
            <CardTitle>Pending WFH requests</CardTitle>
          </CardHeader>
          <CardContent>
            {q.data && q.data.wfh_requests.length > 0 ? (
              <ul className="divide-y text-sm">
                {q.data.wfh_requests.map((w) => (
                  <li
                    key={w.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-2"
                  >
                    <span>
                      <span className="font-medium">
                        {w.user_name ?? `User #${w.user_id}`}
                      </span>{" "}
                      — {w.start_date} → {w.end_date} ({w.total_days} day
                      {w.total_days === 1 ? "" : "s"})
                      {w.reason ? (
                        <span className="text-muted-foreground"> · {w.reason}</span>
                      ) : null}
                    </span>
                    <span className="flex items-center gap-2">
                      <Badge variant="outline">{w.status}</Badge>
                      <Button
                        size="sm"
                        disabled={approve.isPending}
                        onClick={async () => {
                          try {
                            await approve.mutateAsync({ id: w.id });
                            toast.success("Approved");
                          } catch {
                            toast.error("Failed to approve");
                          }
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reject.isPending}
                        onClick={async () => {
                          try {
                            await reject.mutateAsync({ id: w.id });
                            toast.success("Rejected");
                          } catch {
                            toast.error("Failed to reject");
                          }
                        }}
                      >
                        Reject
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-sm">
                No pending WFH requests.
              </p>
            )}
          </CardContent>
        </Card>
      </LeaveFeatureGate>
    </TimeAndLeaveShell>
  );
}
